import { userAxios } from '../index'

/**
 * Servicio para obtener el plan activo del usuario member
 * @param {number} userId - ID del usuario member
 * @returns {Promise} Respuesta del API con el plan activo
 */
const getActivePlan = async userId => {
  try {
    const response = await userAxios.get(`memberships/active/patient/${userId}`)
    return response.data
  } catch (error) {
    console.error('Error al obtener el plan activo:', error)
    throw error
  }
}

/**
 * Filtra los planes de membresía por rol (ej. rol member = 10).
 * POST user-member-plan/filter/{roleId}
 * @param {number} roleId - ID del rol (member = 10)
 * @returns {Promise<{ status: string, data: Object }>} Respuesta con status y data
 */
const filterByRole = async roleId => {
  try {
    const response = await userAxios.post(`user-member-plan/filter/${roleId}`)
    return response.data
  } catch (error) {
    console.error('Error al filtrar planes por rol:', error)
    throw error
  }
}

/**
 * Crea sesión de Stripe Checkout para una membresía pendiente (usuario logueado).
 * POST user-member-plan/checkout con { membership_id, billing_interval? }
 * @param {number} membershipId
 * @param {string} [billingInterval] month | quarter | year
 * @returns {Promise<{ checkoutUrl: string }>}
 */
const createCheckout = async (membershipId, billingInterval) => {
  try {
    const body = { membership_id: membershipId }
    if (billingInterval) body.billing_interval = billingInterval
    const response = await userAxios.post('user-member-plan/checkout', body)
    return response.data
  } catch (error) {
    console.error('Error al crear checkout:', error)
    throw error
  }
}

/**
 * Cancela una membresía (sigue activa hasta cancel_at).
 * POST user-member-plan/cancel
 * @param {number} membershipId
 * @returns {Promise}
 */
const cancelMembership = async membershipId => {
  try {
    const response = await userAxios.post('user-member-plan/cancel', {
      membership_id: membershipId
    })
    return response.data
  } catch (error) {
    console.error('Error al cancelar la membresía:', error)
    throw error
  }
}

/**
 * Reanuda una cancelación programada.
 * POST user-member-plan/resume
 * @param {number} membershipId
 * @returns {Promise}
 */
const resumeMembership = async membershipId => {
  try {
    const response = await userAxios.post('user-member-plan/resume', {
      membership_id: membershipId
    })
    return response.data
  } catch (error) {
    console.error('Error al reanudar la membresía:', error)
    throw error
  }
}

/**
 * Grupo familiar del titular.
 * GET /api/user-member-plan/family/{membership_id}
 * @param {number} membershipId
 */
const getFamily = async membershipId => {
  const response = await userAxios.get(`user-member-plan/family/${membershipId}`)
  return response.data
}

/**
 * Alta de integrante. Si el cobro falla, state es pending_payment y llega pending_invoice_url.
 * POST /api/user-member-plan/family/{membership_id}/members
 * @param {number} membershipId
 * @param {Object} member
 */
const addFamilyMember = async (membershipId, member) => {
  const response = await userAxios.post(`user-member-plan/family/${membershipId}/members`, member)
  return response.data
}

/**
 * Baja de integrante. Devuelve la lista actualizada.
 * DELETE /api/user-member-plan/family/{membership_id}/members/{member_membership_id}
 * @param {number} membershipId
 * @param {number} memberMembershipId
 */
const removeFamilyMember = async (membershipId, memberMembershipId) => {
  const response = await userAxios.delete(
    `user-member-plan/family/${membershipId}/members/${memberMembershipId}`
  )
  return response.data
}

/** Empresas que administra el usuario logueado. GET /api/user-member-plan/companies */
const getCompanies = async () => {
  const response = await userAxios.get('user-member-plan/companies')
  return response.data
}

/** Detalle de empresa, cupos y empleados. GET /api/user-member-plan/company/{id} */
const getCompany = async companyId => {
  const response = await userAxios.get(`user-member-plan/company/${companyId}`)
  return response.data
}

/** Alta de empleado. POST /api/user-member-plan/company/{id}/employees */
const addCompanyEmployee = async (companyId, employee) => {
  const response = await userAxios.post(`user-member-plan/company/${companyId}/employees`, employee)
  return response.data
}

/** Baja de empleado. El cupo queda libre. */
const removeCompanyEmployee = async (companyId, membershipId) => {
  const response = await userAxios.delete(
    `user-member-plan/company/${companyId}/employees/${membershipId}`
  )
  return response.data
}

/** Reduce cupos desde la siguiente renovación. POST .../seats { quantity } */
const updateCompanySeats = async (companyId, quantity) => {
  const response = await userAxios.post(`user-member-plan/company/${companyId}/seats`, { quantity })
  return response.data
}

const cancelCompany = async companyId => {
  const response = await userAxios.post(`user-member-plan/company/${companyId}/cancel`)
  return response.data
}

const resumeCompany = async companyId => {
  const response = await userAxios.post(`user-member-plan/company/${companyId}/resume`)
  return response.data
}

/** Checkout de una empresa sin pagar. */
const checkoutCompany = async companyId => {
  const response = await userAxios.post(`user-member-plan/company/${companyId}/checkout`)
  return response.data
}

/**
 * Elegibilidad de la garantía de 30 días.
 * GET /api/user-member-plan/guarantee/{subscription}
 * @param {number} subscriptionId
 */
const getGuarantee = async subscriptionId => {
  const response = await userAxios.get(`user-member-plan/guarantee/${subscriptionId}`)
  return response.data
}

/** Solicita la garantía. POST con { reason? }. */
const requestGuarantee = async (subscriptionId, reason) => {
  const body = {}
  if (reason && String(reason).trim()) body.reason = String(reason).trim()
  const response = await userAxios.post(`user-member-plan/guarantee/${subscriptionId}`, body)
  return response.data
}

export default {
  getActivePlan,
  filterByRole,
  createCheckout,
  cancelMembership,
  resumeMembership,
  getFamily,
  addFamilyMember,
  removeFamilyMember,
  getCompanies,
  getCompany,
  addCompanyEmployee,
  removeCompanyEmployee,
  updateCompanySeats,
  cancelCompany,
  resumeCompany,
  checkoutCompany,
  getGuarantee,
  requestGuarantee
}
